"use client";

import { convertToAssetId, HydratedInterface, Member } from "@zapier/client";
import { InterfaceDeleteModal } from "../InterfaceDeleteModal";
import { InterfaceDuplicateModal } from "../InterfaceDuplicateModal";
import { InterfaceChangeOwnerModal } from "../InterfaceChangeOwnerModal";
import { InterfaceRenameModal } from "../InterfaceRenameModal";
import { InterfaceShareModal } from "../InterfaceShareModal";
import { useMemo, useState } from "react";
import { AssetTypes } from "@zapier/utils";
import { MoveAssetModal } from "@zapier/shared";

export enum InterfaceModal {
  Delete = "interface__delete",
  Duplicate = "interface__duplicate",
  ChangeOwner = "interface__changeOwner",
  Share = "interface__share",
  Rename = "interface__rename",
  Move = "interface__move",
}

interface Props {
  currentModal: InterfaceModal | undefined;
  currentInterface: HydratedInterface | undefined;
  setCurrentInterface: (
    interfaceObject?: HydratedInterface | undefined,
  ) => void;
  setCurrentModal: (modal?: InterfaceModal | undefined) => void;
  folderId?: string; // used for cache invalidation in mutations
}

export function InterfacesModals({
  currentModal,
  currentInterface,
  setCurrentInterface,
  setCurrentModal,
  folderId,
}: Props) {
  const handleCloseModal = () => {
    setCurrentModal(undefined);
    setCurrentInterface(undefined);
    setNewOwner(undefined);
  };

  const [newOwner, setNewOwner] = useState<Member | undefined>();

  const assetIds = useMemo(() => {
    if (!currentInterface?.id) {
      return;
    }
    return [convertToAssetId(AssetTypes.Interface, currentInterface.id)];
  }, [currentInterface?.id]);

  return (
    <>
      <InterfaceDeleteModal
        isOpen={currentModal === InterfaceModal.Delete}
        onClose={handleCloseModal}
        interfaceObj={currentInterface}
        folderId={folderId}
      />

      <InterfaceDuplicateModal
        isOpen={currentModal === InterfaceModal.Duplicate}
        onClose={handleCloseModal}
        interfaceObj={currentInterface}
        folderId={folderId}
      />

      <InterfaceChangeOwnerModal
        isOpen={currentModal === InterfaceModal.ChangeOwner}
        onClose={handleCloseModal}
        interfaceObj={currentInterface}
        preselectedUser={newOwner}
        folderId={folderId}
      />

      <InterfaceShareModal
        isOpen={currentModal === InterfaceModal.Share}
        onCloseModal={handleCloseModal}
        currentInterface={currentInterface}
        onChangeOwner={(newOwner) => {
          setNewOwner(newOwner);
          setCurrentModal(InterfaceModal.ChangeOwner);
        }}
      />

      <InterfaceRenameModal
        isOpen={currentModal === InterfaceModal.Rename}
        onClose={handleCloseModal}
        interfaceObj={currentInterface}
        folderId={folderId}
      />

      <MoveAssetModal
        assetType={AssetTypes.Interface}
        assetIds={assetIds}
        assetTitle={currentInterface?.name}
        isOpen={currentModal === InterfaceModal.Move}
        setIsOpen={handleCloseModal}
        assetParentId={folderId}
      />
    </>
  );
}
